// components/PayrollAuthGuard.tsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePayrollAuth } from '../hook/usePayrollAuth';
import PayrollAuthModal from '../components/PayrollAuthModal';
import { usePayrollAuthContext } from '../context/PayrollAuthContext';
import {
    isDecisionLockSection,
    isScheduleWriteRoute,
    resolveLockSectionFromPath,
    resolveScheduleRouteActionSection,
} from '../lib/lockedSections';
import { getItemWithExpiration } from '../lib/utils';

export const PayrollAuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const authObject = getItemWithExpiration('authObject');
    const isCenterViewer = authObject?.accessMode === 'view' || authObject?.role === 'center_viewer';
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoading, isPasswordSet, verifyPassword, setPassword } = usePayrollAuth();
    const { isSectionUnlocked, isSectionLocked, unlockSection, setLastSection, syncAccount, getLockSupport } =
        usePayrollAuthContext();
    const [step, setStep] = useState<'set' | 'confirm'>(() => (isPasswordSet ? 'confirm' : 'set'));

    const currentSection = resolveLockSectionFromPath(location.pathname, location.search);
    /*
     * 생성·수정 화면은 보기(currentSection = schedule.calendar)와 동작(생성 또는 수정) 둘 중
     * **하나라도** 잠기면 묻는다. 인증은 30분 전역 공유라 비밀번호는 한 번이다.
     * 회원 상세·손실 시그널의 「수업 등록」 이동도 모두 이 경로라 여기서 함께 덮인다.
     */
    const actionSection = resolveScheduleRouteActionSection(location.pathname, location.state);

    /*
     * 잠금 상태가 **어느 계정 것인지** 맞춘다.
     * 잠금 컨텍스트는 라우터 바깥이라 로그아웃·계정 전환에 다시 그려지지 않는다 —
     * 알려 주지 않으면 앞 계정의 잠금 상태로 이 화면을 열게 된다.
     */
    useEffect(() => {
        syncAccount(authObject?.adminId ?? null);
    }, [authObject?.adminId, syncAccount]);

    useEffect(() => {
        setStep(isPasswordSet ? 'confirm' : 'set');
    }, [isPasswordSet]);

    // 센터 열람 토큰은 서버에서 급여/정산 조회만 허용하며, 개인 2차 비밀번호는 요구하지 않는다.
    if (isCenterViewer) return <>{children}</>;

    /*
     * --- 0) 손실 시그널·기프티콘은 잠금 계약 판정이 끝날 때까지 기다린다 ---
     * 판정 전(unknown)에는 두 키를 잠긴 것으로 보는데, 그대로 내려가면 서버에서 열어 둔 계정도
     * 비밀번호 창이 한 번 번쩍 뜬다. 그렇다고 아래 1) 에서 통과시키면 잠긴 계정이 열린다.
     * 그래서 「잠금 대상 아니면 바로 렌더」보다 **먼저**, 이 두 섹션만 대기한다. 다른 섹션은 막지 않는다.
     * (조회가 실패하면 legacy 로 내려가므로 영원히 기다리지 않는다)
     */
    if ((isDecisionLockSection(currentSection) || !!actionSection) && getLockSupport() === 'unknown') {
        return null;
    }

    // --- 1) 잠금 대상이 아니면 바로 렌더 ---
    if (!isSectionLocked(currentSection) && !(actionSection && isSectionLocked(actionSection))) {
        return <>{children}</>;
    }

    // --- 2) 30분 이내에 인증된 섹션이면 바로 통과 ---
    if (isSectionUnlocked(currentSection)) {
        return <>{children}</>;
    }

    // --- 3) 인증 로딩 중이면 스피너(또는 null) ---
    if (isLoading) {
        return null;
    }

    // --- 4) 그 외: 비밀번호 모달 띄우기 ---
    return (
        <PayrollAuthModal
            step={step}
            onSubmit={async (pw: string) => {
                try {
                    const num = pw;

                    /*
                     * 설정·확인은 같은 요청이다. 서버 답으로 가른다: 200 = 맞음(인증), 201 = 새로 만듦(인증 아님).
                     * 첫 조회가 실패해 설정 여부를 모르면 '설정' 단계로 보이는데, 그때 맞는 비밀번호의 200 을
                     * 버리면 생성·수정 화면에 영영 못 들어간다.
                     */
                    const result = await (isPasswordSet ? verifyPassword : setPassword)(num);
                    if (result?.status === 200) {
                        unlockSection(currentSection);
                        setLastSection(currentSection);
                    }
                } catch {
                    alert('비밀번호가 올바르지 않습니다.');
                }
            }}
            onCancel={() => {
                /*
                 * 생성·수정 화면은 회원 상세 등에서 들어온다 — 홈으로 보내면 보던 화면을 잃는다.
                 * 앱 안에서 넘어온 이전 화면이 있으면 뒤로, 없으면(주소 직접 입력·새 탭) 스케줄로.
                 * (react-router 는 첫 진입 위치의 key 를 'default' 로 둔다)
                 */
                if (isScheduleWriteRoute(location.pathname)) {
                    if (location.key && location.key !== 'default') navigate(-1);
                    else navigate('/schedule');
                    return;
                }
                navigate('/');
            }}
        />
    );
};
